<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div class="mb-8 border-b-2 border-yellow-400 pb-4">
                <div class="flex items-center gap-3 mb-2">
                    <span class="bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded">DIAGNOSTIC TOOL</span>
                    <h1 class="font-header text-3xl font-bold text-primary">Test Orders</h1>
                </div>
                <p class="text-gray-600 text-sm">
                    Diagnostic page to analyze order receipt discrepancies. All orders and their receipts are logged to
                    the browser console as JSON on load. Use <strong>Compare</strong> to see a three-way breakdown:
                    Stripe&nbsp;/ DB&nbsp;/ runtime recalculation.
                </p>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-16">
                <div class="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p class="text-gray-600">Loading orders...</p>
            </div>

            <!-- Error -->
            <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <p class="text-red-700 font-semibold">Error loading orders</p>
                <p class="text-red-600 text-sm mt-1">{{ error }}</p>
                <p class="text-gray-500 text-xs mt-2">Make sure you are logged in as an administrator.</p>
                <button @click="loadOrders"
                    class="mt-4 px-4 py-2 bg-primary text-white rounded text-sm hover:opacity-90">
                    Retry
                </button>
            </div>

            <!-- Orders table -->
            <div v-else>
                <div class="flex items-center justify-between mb-4">
                    <p class="text-gray-700 font-medium">
                        {{ orders.length }} order{{ orders.length !== 1 ? 's' : '' }} loaded
                        <span v-if="pagination" class="text-gray-500 text-sm ml-2">
                            (page {{ pagination.page }} of {{ pagination.total_pages }}, {{ pagination.total }} total)
                        </span>
                    </p>
                    <div class="flex gap-2">
                        <button @click="logAllReceiptsToConsole"
                            class="px-4 py-2 bg-yellow-400 text-black rounded text-sm font-semibold hover:bg-yellow-500">
                            Log All to Console
                        </button>
                        <button v-if="pagination && pagination.next_page" @click="loadNextPage"
                            class="px-4 py-2 bg-primary text-white rounded text-sm hover:opacity-90">
                            Load Next Page
                        </button>
                    </div>
                </div>

                <div v-if="orders.length === 0" class="bg-gray-50 rounded-lg p-8 text-center">
                    <p class="text-gray-600">No orders found.</p>
                </div>

                <div v-else class="space-y-4">
                    <div v-for="order in orders" :key="order.id"
                        class="border border-gray-200 rounded-lg overflow-hidden">
                        <!-- Order header -->
                        <div class="bg-gray-50 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <span class="font-bold text-primary">Order #{{ order.order_number }}</span>
                                <span class="ml-3 text-gray-500 text-sm">{{ formatDate(order.created_at) }}</span>
                                <span :class="paymentStatusClass(order.payment_status)"
                                    class="ml-3 px-2 py-0.5 rounded-full text-xs font-semibold">
                                    {{ order.payment_status }}
                                </span>
                                <span :class="statusClass(order.status)"
                                    class="ml-1 px-2 py-0.5 rounded-full text-xs font-semibold">
                                    {{ order.status }}
                                </span>
                            </div>
                            <div class="text-right">
                                <span class="text-gray-700 text-sm">Customer: </span>
                                <span class="font-medium text-primary text-sm">{{ order.customer_name || 'Guest' }}</span>
                                <span class="mx-2 text-gray-300">|</span>
                                <span class="text-gray-700 text-sm">Total: </span>
                                <span class="font-bold text-primary">${{ parseFloat(order.total_amount).toFixed(2) }}</span>
                                <span v-if="parseFloat(order.discount_amount) > 0"
                                    class="ml-2 text-xs text-green-600">
                                    -${{ parseFloat(order.discount_amount).toFixed(2) }} discount
                                </span>
                            </div>
                        </div>

                        <!-- Order body -->
                        <div class="px-6 py-4">
                            <!-- Payment IDs -->
                            <div class="mb-3 text-xs text-gray-500 space-y-1">
                                <div v-if="order.stripe_payment_intent_id">
                                    <span class="font-medium">Stripe PI:</span>
                                    <code class="ml-1 bg-gray-100 px-1 py-0.5 rounded">{{ order.stripe_payment_intent_id }}</code>
                                </div>
                                <div v-if="order.paypal_order_id">
                                    <span class="font-medium">PayPal Order ID:</span>
                                    <code class="ml-1 bg-gray-100 px-1 py-0.5 rounded">{{ order.paypal_order_id }}</code>
                                </div>
                                <div>
                                    <span class="font-medium">Payment method:</span>
                                    <span class="ml-1">{{ order.payment_method || 'N/A' }}</span>
                                </div>
                            </div>

                            <!-- Items summary -->
                            <div v-if="order.items && order.items.length > 0" class="mb-4">
                                <p class="text-xs font-semibold text-gray-500 uppercase mb-2">Items</p>
                                <div class="space-y-1">
                                    <div v-for="(item, i) in order.items" :key="i"
                                        class="flex justify-between text-sm text-gray-700">
                                        <span>{{ item.name || item.book_title }}
                                            <span v-if="item.format" class="text-gray-400">({{ item.format }})</span>
                                            <span v-if="item.quantity > 1" class="text-gray-500"> x{{ item.quantity }}</span>
                                        </span>
                                        <span>${{ parseFloat(item.total_price || item.unit_price).toFixed(2) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Receipt actions -->
                            <div class="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                                <!-- Log single order to console -->
                                <button @click="logOrderToConsole(order)"
                                    class="px-3 py-1.5 text-xs bg-gray-800 text-white rounded hover:bg-gray-700">
                                    Log JSON
                                </button>

                                <!-- Download site receipt -->
                                <button @click="downloadSiteReceipt(order)"
                                    :disabled="downloadingReceipt[order.id]"
                                    class="px-3 py-1.5 text-xs bg-primary text-white rounded hover:opacity-90 disabled:opacity-50">
                                    {{ downloadingReceipt[order.id] ? 'Generating...' : 'Download Site Receipt' }}
                                </button>

                                <!-- Fetch Stripe receipt -->
                                <button v-if="order.stripe_payment_intent_id"
                                    @click="openStripeReceipt(order)"
                                    :disabled="fetchingStripeReceipt[order.id]"
                                    class="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                                    {{ fetchingStripeReceipt[order.id] ? 'Fetching...' : 'Open Stripe Receipt' }}
                                </button>

                                <!-- Stripe receipt result -->
                                <span v-if="stripeReceiptResults[order.id] === null"
                                    class="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded">
                                    No Stripe receipt found
                                </span>

                                <!-- Three-way compare button -->
                                <button @click="toggleCompare(order)"
                                    :disabled="comparingOrder[order.id]"
                                    class="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                                    {{ comparingOrder[order.id] ? 'Loading…'
                                        : compareResults[order.id] ? 'Hide Compare'
                                        : 'Compare' }}
                                </button>
                            </div>

                            <!-- ── Three-way comparison panel ── -->
                            <div v-if="compareResults[order.id] && !comparingOrder[order.id]"
                                class="mt-4 border border-emerald-200 rounded-lg overflow-hidden">

                                <!-- Panel header -->
                                <div class="bg-emerald-50 px-4 py-2 flex items-center justify-between">
                                    <span class="text-xs font-bold text-emerald-800 uppercase tracking-wide">
                                        Order Comparison — #{{ compareResults[order.id].order_number }}
                                    </span>
                                    <span class="text-xs text-gray-500">
                                        Payment method: {{ compareResults[order.id].payment_method || 'N/A' }}
                                    </span>
                                </div>

                                <!-- Discrepancy summary bar -->
                                <div class="bg-white px-4 py-3 border-b border-gray-100">
                                    <p class="text-xs font-semibold text-gray-500 uppercase mb-2">Discrepancy Summary (cents)</p>
                                    <div class="grid grid-cols-3 gap-3 text-xs">
                                        <div class="rounded p-2"
                                            :class="discrepancyClass(compareResults[order.id].discrepancy.stripe_received_vs_db_cents)">
                                            <div class="font-semibold mb-0.5">Stripe received vs DB</div>
                                            <div class="font-mono text-base">
                                                {{ formatDiscrepancy(compareResults[order.id].discrepancy.stripe_received_vs_db_cents) }}
                                            </div>
                                        </div>
                                        <div class="rounded p-2"
                                            :class="discrepancyClass(compareResults[order.id].discrepancy.stripe_received_vs_runtime_cents)">
                                            <div class="font-semibold mb-0.5">Stripe received vs runtime</div>
                                            <div class="font-mono text-base">
                                                {{ formatDiscrepancy(compareResults[order.id].discrepancy.stripe_received_vs_runtime_cents) }}
                                            </div>
                                        </div>
                                        <div class="rounded p-2"
                                            :class="discrepancyClass(compareResults[order.id].discrepancy.db_vs_runtime_cents)">
                                            <div class="font-semibold mb-0.5">DB vs runtime</div>
                                            <div class="font-mono text-base">
                                                {{ formatDiscrepancy(compareResults[order.id].discrepancy.db_vs_runtime_cents) }}
                                            </div>
                                        </div>
                                    </div>
                                    <p class="text-xs text-gray-400 mt-2">
                                        DB total: <strong>{{ compareResults[order.id].discrepancy.db_total_cents }}¢
                                        (${{ (compareResults[order.id].discrepancy.db_total_cents / 100).toFixed(2) }})</strong>
                                        &nbsp;·&nbsp;
                                        Runtime total: <strong>{{ compareResults[order.id].discrepancy.runtime_total_cents }}¢
                                        (${{ (compareResults[order.id].discrepancy.runtime_total_cents / 100).toFixed(2) }})</strong>
                                        <span v-if="compareResults[order.id].discrepancy.stripe_received_cents !== null">
                                            &nbsp;·&nbsp;
                                            Stripe received: <strong>{{ compareResults[order.id].discrepancy.stripe_received_cents }}¢
                                            (${{ (compareResults[order.id].discrepancy.stripe_received_cents / 100).toFixed(2) }})</strong>
                                        </span>
                                    </p>
                                </div>

                                <!-- Three columns -->
                                <div class="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">

                                    <!-- Col 1: Stripe -->
                                    <div class="p-4">
                                        <p class="text-xs font-bold text-indigo-700 uppercase mb-2">① Stripe</p>
                                        <template v-if="compareResults[order.id].stripe.available">
                                            <table class="w-full text-xs">
                                                <tbody>
                                                <tr>
                                                    <td class="py-0.5 text-gray-500 pr-2">PI ID</td>
                                                    <td class="py-0.5 font-mono break-all">{{ compareResults[order.id].stripe.payment_intent_id }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-0.5 text-gray-500 pr-2">Status</td>
                                                    <td class="py-0.5 font-semibold">{{ compareResults[order.id].stripe.status }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-0.5 text-gray-500 pr-2">Amount intended</td>
                                                    <td class="py-0.5">{{ compareResults[order.id].stripe.amount_cents }}¢ (${{ compareResults[order.id].stripe.amount_dollars }})</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-0.5 text-gray-500 pr-2">Amount received</td>
                                                    <td class="py-0.5 font-bold text-indigo-700">{{ compareResults[order.id].stripe.amount_received_cents }}¢ (${{ compareResults[order.id].stripe.amount_received_dollars }})</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-0.5 text-gray-500 pr-2">Currency</td>
                                                    <td class="py-0.5 uppercase">{{ compareResults[order.id].stripe.currency }}</td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </template>
                                        <template v-else>
                                            <p class="text-xs text-red-600">{{ compareResults[order.id].stripe.error }}</p>
                                        </template>
                                    </div>

                                    <!-- Col 2: Database -->
                                    <div class="p-4">
                                        <p class="text-xs font-bold text-blue-700 uppercase mb-2">② Database</p>
                                        <table class="w-full text-xs">
                                            <tbody>
                                            <tr>
                                                <td class="py-0.5 text-gray-500 pr-2">Total</td>
                                                <td class="py-0.5 font-bold text-blue-700">${{ compareResults[order.id].database.total_amount }} ({{ compareResults[order.id].database.total_amount_cents }}¢)</td>
                                            </tr>
                                            <tr>
                                                <td class="py-0.5 text-gray-500 pr-2">Subtotal</td>
                                                <td class="py-0.5">${{ compareResults[order.id].database.subtotal }}</td>
                                            </tr>
                                            <tr>
                                                <td class="py-0.5 text-gray-500 pr-2">Discount</td>
                                                <td class="py-0.5">-${{ compareResults[order.id].database.discount_amount }}
                                                    <span v-if="compareResults[order.id].database.discount_code" class="ml-1 text-green-600">({{ compareResults[order.id].database.discount_code }})</span>
                                                </td>
                                            </tr>
                                            <tr v-if="parseFloat(compareResults[order.id].database.tax_amount) > 0">
                                                <td class="py-0.5 text-gray-500 pr-2">Tax</td>
                                                <td class="py-0.5">${{ compareResults[order.id].database.tax_amount }}</td>
                                            </tr>
                                            <tr v-if="parseFloat(compareResults[order.id].database.shipping_amount) > 0">
                                                <td class="py-0.5 text-gray-500 pr-2">Shipping</td>
                                                <td class="py-0.5">${{ compareResults[order.id].database.shipping_amount }}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        <p class="text-xs font-semibold text-gray-500 uppercase mt-2 mb-1">Items</p>
                                        <div class="space-y-0.5">
                                            <div v-for="(item, i) in compareResults[order.id].database.items" :key="i"
                                                class="text-xs flex justify-between text-gray-700">
                                                <span>{{ item.name || item.book_title }}
                                                    <span v-if="item.format" class="text-gray-400">({{ item.format }})</span>
                                                    <span v-if="item.quantity > 1"> ×{{ item.quantity }}</span>
                                                </span>
                                                <span>${{ parseFloat(item.total_price || item.unit_price).toFixed(2) }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Col 3: Runtime -->
                                    <div class="p-4">
                                        <p class="text-xs font-bold text-emerald-700 uppercase mb-2">③ Runtime (current logic)</p>
                                        <table class="w-full text-xs">
                                            <tbody>
                                            <tr>
                                                <td class="py-0.5 text-gray-500 pr-2">Total</td>
                                                <td class="py-0.5 font-bold text-emerald-700">${{ compareResults[order.id].runtime.total.toFixed(2) }} ({{ compareResults[order.id].runtime.total_cents }}¢)</td>
                                            </tr>
                                            <tr>
                                                <td class="py-0.5 text-gray-500 pr-2">Subtotal</td>
                                                <td class="py-0.5">${{ compareResults[order.id].runtime.subtotal.toFixed(2) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="py-0.5 text-gray-500 pr-2">Discount</td>
                                                <td class="py-0.5">-${{ compareResults[order.id].runtime.discount_amount.toFixed(2) }}
                                                    <span v-if="compareResults[order.id].runtime.discount_code" class="ml-1 text-green-600">({{ compareResults[order.id].runtime.discount_code }})</span>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        <p class="text-xs font-semibold text-gray-500 uppercase mt-2 mb-1">Items (current prices)</p>
                                        <div class="space-y-0.5">
                                            <div v-for="(item, i) in compareResults[order.id].runtime.items" :key="i"
                                                class="text-xs text-gray-700">
                                                <div v-if="item.error" class="text-red-500">{{ item.name }}: {{ item.error }}</div>
                                                <div v-else class="flex justify-between">
                                                    <span>{{ item.name }}
                                                        <span v-if="item.quantity > 1"> ×{{ item.quantity }}</span>
                                                        <span v-if="item.original_unit_price && item.original_unit_price !== item.unit_price"
                                                            class="ml-1 text-gray-400 line-through">${{ item.original_unit_price.toFixed(2) }}</span>
                                                    </span>
                                                    <span>${{ (item.item_subtotal).toFixed(2) }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- end comparison panel -->

                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="pagination && pagination.total_pages > 1" class="mt-6 flex justify-center gap-2">
                    <button @click="loadPage(pagination.page - 1)" :disabled="!pagination.prev_page"
                        class="px-4 py-2 text-sm border rounded disabled:opacity-40 hover:bg-gray-50">
                        Previous
                    </button>
                    <span class="px-4 py-2 text-sm text-gray-600">
                        Page {{ pagination.page }} of {{ pagination.total_pages }}
                    </span>
                    <button @click="loadPage(pagination.page + 1)" :disabled="!pagination.next_page"
                        class="px-4 py-2 text-sm border rounded disabled:opacity-40 hover:bg-gray-50">
                        Next
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../services/admin'
import { generateReceiptHTML } from '../utils/receipt.js'
import { formatDate, formatDateForFilename } from '../utils/date'
import html2pdf from 'html2pdf.js'

const loading = ref(true)
const error = ref(null)
const orders = ref([])
const pagination = ref(null)
const downloadingReceipt = ref({})
const fetchingStripeReceipt = ref({})
const stripeReceiptResults = ref({})
const comparingOrder = ref({})
const compareResults = ref({})

async function loadOrders (page = 1) {
    loading.value = true
    error.value = null
    try {
        const result = await adminService.getSales(page, 50)
        if (result && result.data) {
            orders.value = result.data
            pagination.value = result.pagination || null
            logAllReceiptsToConsole()
        } else {
            orders.value = []
            pagination.value = null
        }
    } catch (e) {
        error.value = e.response?.data?.error || e.message || 'Failed to load orders'
        console.error('[TestOrdersPage] Failed to load orders:', e)
    } finally {
        loading.value = false
    }
}

async function loadPage (page) {
    await loadOrders(page)
}

async function loadNextPage () {
    if (pagination.value && pagination.value.next_page) {
        await loadOrders(pagination.value.page + 1)
    }
}

function logAllReceiptsToConsole () {
    console.group('[TestOrdersPage] All orders as JSON')
    orders.value.forEach(order => {
        console.log(JSON.stringify(order, null, 2))
    })
    console.groupEnd()
    console.log('[TestOrdersPage] Full orders array:', JSON.parse(JSON.stringify(orders.value)))
}

function logOrderToConsole (order) {
    console.group(`[TestOrdersPage] Order #${order.order_number}`)
    console.log(JSON.stringify(order, null, 2))
    console.groupEnd()
}

/**
 * Converts a sale record from the admin API to the receipt group format
 * expected by generateReceiptHTML.
 */
function saleToReceiptGroup (order) {
    const items = (order.items || []).map(item => ({
        book_title: item.name || item.book_title || '',
        book_author: item.author || '',
        format: item.format || '',
        price: item.unit_price || 0,
        quantity: item.quantity || 1,
        original_price: item.original_price || null,
        discount_percentage: item.discount_percentage || null
    }))

    return {
        transaction_id: order.order_number,
        purchase_date: order.created_at,
        items,
        subtotal: parseFloat(order.subtotal) || 0,
        discount_amount: parseFloat(order.discount_amount) || 0,
        discount_code: order.discount_code || null,
        total: parseFloat(order.total_amount) || 0,
        shipping_address: order.shipping_address || null,
        billing_address: order.billing_address || null,
        customer_name: order.customer_name || null,
        delivery_option: order.delivery_option || null
    }
}

async function downloadSiteReceipt (order) {
    downloadingReceipt.value = { ...downloadingReceipt.value, [order.id]: true }
    try {
        const group = saleToReceiptGroup(order)
        const receiptHTML = generateReceiptHTML(group, false, null, null)

        const tempWindow = window.open('', '_blank')
        if (!tempWindow) {
            alert('Popups are blocked. Please allow popups for this site in your browser settings, then click "Download Site Receipt" again.')
            return
        }

        tempWindow.document.write(receiptHTML)
        tempWindow.document.close()

        await new Promise(resolve => setTimeout(resolve, 500))

        const element =
            tempWindow.document.querySelector('.receipt-container') ||
            tempWindow.document.body

        const opt = {
            margin: 0.5,
            filename: `receipt-${order.order_number}-${formatDateForFilename(order.created_at)}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight
            },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        }

        await html2pdf().set(opt).from(element).save()
        tempWindow.close()
    } catch (e) {
        console.error('[TestOrdersPage] Error generating site receipt PDF:', e)
        alert(`Failed to generate PDF: ${e.message || 'Unknown error'}. Check the browser console for details.`)
    } finally {
        downloadingReceipt.value = { ...downloadingReceipt.value, [order.id]: false }
    }
}

async function openStripeReceipt (order) {
    if (!order.stripe_payment_intent_id) return

    fetchingStripeReceipt.value = { ...fetchingStripeReceipt.value, [order.id]: true }
    try {
        const result = await adminService.getStripeReceipt(order.stripe_payment_intent_id)

        console.group(`[TestOrdersPage] Stripe receipt for Order #${order.order_number}`)
        console.log('Payment Intent ID:', order.stripe_payment_intent_id)
        console.log(JSON.stringify(result, null, 2))
        console.groupEnd()

        if (result.receipt_url) {
            window.open(result.receipt_url, '_blank')
        } else {
            stripeReceiptResults.value = { ...stripeReceiptResults.value, [order.id]: null }
            console.warn(
                `[TestOrdersPage] No receipt URL found for Order #${order.order_number}`,
                result
            )
        }
    } catch (e) {
        console.error('[TestOrdersPage] Error fetching Stripe receipt:', e)
        const msg = e.response?.data?.error || e.message || 'Unknown error'
        alert(`Failed to fetch Stripe receipt: ${msg}. Check the browser console for details.`)
    } finally {
        fetchingStripeReceipt.value = { ...fetchingStripeReceipt.value, [order.id]: false }
    }
}

/**
 * Toggles the three-way comparison panel for an order.
 * Fetches data from the server on first open; hides on subsequent clicks.
 */
async function toggleCompare (order) {
    if (compareResults.value[order.id]) {
        // Already loaded — toggle visibility by clearing
        const updated = { ...compareResults.value }
        delete updated[order.id]
        compareResults.value = updated
        return
    }

    comparingOrder.value = { ...comparingOrder.value, [order.id]: true }
    try {
        const result = await adminService.compareOrder(order.id)
        console.group(`[TestOrdersPage] Order comparison for #${order.order_number}`)
        console.log(JSON.stringify(result, null, 2))
        console.groupEnd()
        compareResults.value = { ...compareResults.value, [order.id]: result }
    } catch (e) {
        console.error('[TestOrdersPage] Error fetching comparison:', e)
        const msg = e.response?.data?.error || e.message || 'Unknown error'
        alert(`Failed to compare order: ${msg}. Check the browser console for details.`)
    } finally {
        comparingOrder.value = { ...comparingOrder.value, [order.id]: false }
    }
}

/** Returns a Tailwind colour class for a discrepancy value (in cents). */
function discrepancyClass (cents) {
    if (cents === null || cents === undefined) return 'bg-gray-100 text-gray-500'
    if (cents === 0) return 'bg-green-50 text-green-700'
    return 'bg-red-50 text-red-700'
}

/** Formats a discrepancy value; null → 'N/A'. */
function formatDiscrepancy (cents) {
    if (cents === null || cents === undefined) return 'N/A'
    const sign = cents >= 0 ? '+' : ''
    return `${sign}${cents}¢`
}

function paymentStatusClass (status) {
    if (status === 'paid') return 'bg-green-100 text-green-700'
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700'
    if (status === 'failed') return 'bg-red-100 text-red-700'
    if (status === 'refunded') return 'bg-purple-100 text-purple-700'
    return 'bg-gray-100 text-gray-600'
}

function statusClass (status) {
    if (status === 'completed') return 'bg-green-100 text-green-700'
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700'
    if (status === 'cancelled') return 'bg-red-100 text-red-700'
    if (status === 'processing') return 'bg-blue-100 text-blue-700'
    return 'bg-gray-100 text-gray-600'
}

onMounted(() => {
    loadOrders()
})
</script>
